import React, { useEffect, useState, useCallback } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button, Box, Typography, Chip, IconButton, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Tooltip, Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import RestoreIcon from '@mui/icons-material/Restore';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import AdminPageLayout from '../components/admin/AdminPageLayout';
import SectionBlock from '../components/SectionBlock';
import { useAuth } from '../contexts/AuthContext';

const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

// ── Edit dialog ──────────────────────────────────────────────────────────────

function UserEditDialog({ open, user, onClose, onSave, roles }) {
  const [form, setForm] = useState(user || {});
  useEffect(() => { setForm(user || {}); }, [user]);
  if (!user) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit User</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField label="First Name" value={form.firstName || ''} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
          <TextField label="Last Name" value={form.lastName || ''} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
          <TextField label="Email" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <TextField label="Phone Number" value={form.phoneNumber || ''} onChange={e => setForm(f => ({ ...f, phoneNumber: e.target.value }))} />
          <TextField select label="Role" value={form.roleId || ''} onChange={e => setForm(f => ({ ...f, roleId: e.target.value }))}>
            {roles.map(opt => <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>)}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave(form)} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Soft-delete confirmation dialog (requires reason) ────────────────────────

function RemoveUserDialog({ open, user, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (open) { setReason(''); setError(''); } }, [open]);

  const handleConfirm = async () => {
    if (!reason.trim()) { setError('Please enter a reason.'); return; }
    setSaving(true);
    try {
      await onConfirm(user.id, reason.trim());
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to remove user.');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Remove User</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <Typography>
            You are about to logically remove <strong>{user.firstName} {user.lastName}</strong> ({user.email}).
            Their data will be preserved but they will no longer be able to log in.
          </Typography>
          <TextField
            label="Reason for removal *"
            multiline
            minRows={2}
            value={reason}
            onChange={e => { setReason(e.target.value); setError(''); }}
            error={!!error}
            helperText={error}
            placeholder="e.g. Employee left the company, duplicate account…"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : <PersonOffIcon />}
        >
          {saving ? 'Removing…' : 'Remove User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Restore confirmation dialog ───────────────────────────────────────────────

function RestoreDialog({ open, user, onClose, onConfirm }) {
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    try { await onConfirm(user.id); onClose(); }
    finally { setSaving(false); }
  };

  if (!user) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Restore User</DialogTitle>
      <DialogContent>
        <Typography>
          Restore <strong>{user.firstName} {user.lastName}</strong> ({user.email})?
          Their account will be reactivated and they will be able to log in again.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="success"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : <RestoreIcon />}
        >
          {saving ? 'Restoring…' : 'Restore'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);

  const [editUser, setEditUser] = useState(null);
  const [removeUser, setRemoveUser] = useState(null);
  const [restoreUser, setRestoreUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const url = showDeleted
        ? `${apiBase}/api/users?includeDeleted=true`
        : `${apiBase}/api/users`;
      const res = await fetch(url, { credentials: 'include', headers: authHeaders() });
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch {
      setUsers([]);
    }
    setLoading(false);
  }, [showDeleted]);

  const fetchRoles = async () => {
    try {
      const res = await fetch(`${apiBase}/api/users/roles`, { credentials: 'include', headers: authHeaders() });
      const data = await res.json();
      setRoles(Array.isArray(data) ? data : []);
    } catch {
      setRoles([]);
    }
  };

  useEffect(() => { fetchUsers(); fetchRoles(); }, [fetchUsers]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleEditSave = async (form) => {
    await fetch(`${apiBase}/api/users/${form.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      credentials: 'include',
      body: JSON.stringify(form)
    });
    setEditUser(null);
    fetchUsers();
  };

  const handleToggleActive = async (user) => {
    await fetch(`${apiBase}/api/users/${user.id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      credentials: 'include',
      body: JSON.stringify({ isActive: !user.isActive })
    });
    fetchUsers();
  };

  const handleSoftDelete = async (id, reason) => {
    const res = await fetch(`${apiBase}/api/users/${id}/soft-delete`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      credentials: 'include',
      body: JSON.stringify({ reason })
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error || 'Failed to remove user');
    }
    fetchUsers();
  };

  const handleRestore = async (id) => {
    await fetch(`${apiBase}/api/users/${id}/restore`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include'
    });
    fetchUsers();
  };

  const handleHardDelete = async (id) => {
    if (!window.confirm('Permanently delete this user and all their data? This cannot be undone.')) return;
    await fetch(`${apiBase}/api/users/delete/${id}`, { method: 'DELETE', credentials: 'include', headers: authHeaders() });
    fetchUsers();
  };

  // ── Grid ──────────────────────────────────────────────────────────────────

  const filtered = users.filter(u => {
    if (showDeleted && !u.deletedAt) return false;
    const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
    return (
      (!filterName || name.includes(filterName.toLowerCase())) &&
      (!filterEmail || (u.email || '').toLowerCase().includes(filterEmail.toLowerCase())) &&
      (!filterRole || u.role?.name === filterRole) &&
      (!filterStatus || (filterStatus === 'active' ? u.isActive : !u.isActive))
    );
  });

  const columns = [
    { field: 'firstName', headerName: 'First Name', flex: 1 },
    { field: 'lastName', headerName: 'Last Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1.5 },
    { field: 'phoneNumber', headerName: 'Phone', flex: 1 },
    {
      field: 'role', headerName: 'Role', flex: 0.8,
      valueGetter: p => p.row.role?.name || '',
      renderCell: p => <Chip label={p.value || '—'} size="small" />
    },
    {
      field: 'isActive', headerName: 'Status', flex: 0.8,
      renderCell: p => p.row.deletedAt
        ? <Chip label="Removed" color="error" size="small" />
        : p.value
          ? <Chip label="Active" color="success" size="small" />
          : <Chip label="Inactive" color="default" size="small" />
    },
    {
      field: 'lastLogin', headerName: 'Last Login', flex: 1.2,
      valueGetter: p => p.row.lastLogin ? new Date(p.row.lastLogin).toLocaleString() : '—'
    },
    ...(showDeleted ? [{
      field: 'deletedAt', headerName: 'Removed At', flex: 1.2,
      valueGetter: p => p.row.deletedAt ? new Date(p.row.deletedAt).toLocaleString() : '—'
    }, {
      field: 'deletionReason', headerName: 'Reason', flex: 1.5,
      renderCell: p => (
        <Tooltip title={p.row.deletionReason || ''}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {p.row.deletionReason || '—'}
          </span>
        </Tooltip>
      )
    }] : []),
    {
      field: 'actions', headerName: 'Actions', flex: showDeleted ? 1 : 1.5, sortable: false,
      renderCell: p => {
        const isRemoved = !!p.row.deletedAt;
        const isSelf = p.row.id === currentUser?.id;

        if (isRemoved) {
          return (
            <Box>
              <Tooltip title="Restore user">
                <IconButton color="success" onClick={() => setRestoreUser(p.row)}>
                  <RestoreIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Permanently delete">
                <IconButton color="error" onClick={() => handleHardDelete(p.row.id)}>
                  <DeleteForeverIcon />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }

        return (
          <Box>
            <Tooltip title="Edit">
              <IconButton onClick={() => setEditUser(p.row)}><EditIcon /></IconButton>
            </Tooltip>
            <Tooltip title={p.row.isActive ? 'Deactivate' : 'Activate'}>
              <span>
                <IconButton onClick={() => handleToggleActive(p.row)} disabled={isSelf}>
                  {p.row.isActive ? <ToggleOffIcon /> : <ToggleOnIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isSelf ? 'Cannot remove your own account' : 'Remove user'}>
              <span>
                <IconButton
                  color="warning"
                  onClick={() => setRemoveUser(p.row)}
                  disabled={isSelf}
                >
                  <PersonOffIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      }
    }
  ];

  return (
    <AdminPageLayout fullWidth>
      <SectionBlock
        title="System Users"
        subtitle="Manage user accounts — edit details, activate/deactivate, or logically remove users. Removed users cannot log in but their data is preserved and can be restored."
      >
        <Box mb={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Search by name"
            value={filterName}
            onChange={e => setFilterName(e.target.value)}
            style={{ minWidth: 200 }}
            placeholder="First or last name…"
          />
          <TextField
            label="Search by email"
            value={filterEmail}
            onChange={e => setFilterEmail(e.target.value)}
            style={{ minWidth: 220 }}
            placeholder="Email address…"
          />
          <TextField select label="Role" value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ minWidth: 120 }}>
            <MenuItem value="">All</MenuItem>
            {roles.map(opt => <MenuItem key={opt.id} value={opt.name}>{opt.name}</MenuItem>)}
          </TextField>
          <TextField select label="Status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ minWidth: 120 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={showDeleted}
                onChange={e => { setShowDeleted(e.target.checked); setFilterStatus(''); }}
                color="error"
              />
            }
            label={<Typography variant="body2" color={showDeleted ? 'error' : 'textSecondary'}>Show Removed Users</Typography>}
          />
          {showDeleted && (
            <Alert severity="warning" sx={{ py: 0, px: 1 }}>
              Viewing removed users. They cannot log in.
            </Alert>
          )}
        </Box>

        {loading ? <CircularProgress /> : (
          <DataGrid
            autoHeight
            rows={filtered}
            columns={columns}
            getRowId={r => r.id}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            getRowClassName={p => p.row.deletedAt ? 'row-removed' : ''}
            sx={{
              '& .row-removed': {
                backgroundColor: '#fff3f3',
                color: '#999',
                textDecoration: 'line-through',
                '&:hover': { backgroundColor: '#ffe0e0' }
              }
            }}
          />
        )}

        <UserEditDialog open={!!editUser} user={editUser} onClose={() => setEditUser(null)} onSave={handleEditSave} roles={roles} />
        <RemoveUserDialog open={!!removeUser} user={removeUser} onClose={() => setRemoveUser(null)} onConfirm={handleSoftDelete} />
        <RestoreDialog open={!!restoreUser} user={restoreUser} onClose={() => setRestoreUser(null)} onConfirm={handleRestore} />
      </SectionBlock>
    </AdminPageLayout>
  );
}
